import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, Redirect, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Button, IconButton, Menu, MenuItem, Paper, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { HorizontalNavBar } from "components";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Notice } from "components/Prompts/Notice";
import { useSocket } from "components/providers/Socket-provider/SocketContext";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { Routes } from "constants/routes";
import { useNavigate, useNavigationHooks, useUrlHooks } from "hooks";
import { useIsViewerPermission, usePrompt } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useQueryHook } from "hooks/shared-hooks/use-query-hook";
import { isEqual } from "lodash";
import { ISocketService, useAccountSelector, useDealActions, useReportsMetaSelector, useUserIdSelector } from "store";
import {
  AltiusErrors,
  ConsolidationMapping,
  IAssetClassMapping,
  IDealsTableRow,
  IMeasureMapping,
  ISecurityTableRow,
  MeasureParameter
} from "store/types";

import { BankStatementUploadModal } from "components/Deal-amortization/BankStatementUploadModal";

import EmptyCardAssets from "../../assets/images/EmptyCardAssets.png";
import EmptyCardList from "../../assets/images/EmptyCardList.png";
import { EmptyCard } from "../Empty-card/EmptyCard";
import { MappingTable } from "./Mapping/MappingTable";
import { SecuritiesFilters, SecuritiesTable } from "./Securities/SecuritiesTable";

type ConsolidationTab = "securities" | "measures" | "assetClasses";

const defaultSecuritiesFilters: SecuritiesFilters = {
  dateRange: { value: "fromInception" },
  filters: {},
  search: ""
};

export const Consolidation: React.FC = () => {
  const { pathname } = useLocation();
  const { queryParams } = useUrlHooks();
  const { setParams, removeParams } = useNavigationHooks();
  const navigate = useNavigate();

  const isConsolidationRoot = Boolean(matchPath(pathname, { path: Routes.consolidation, exact: true }));

  const activeTab: ConsolidationTab = useMemo(() => {
    if (matchPath(pathname, Routes.consolidationMeasures)) return "measures";
    if (matchPath(pathname, Routes.consolidationAssetClasses)) return "assetClasses";
    return "securities";
  }, [pathname]);

  const isOnSecuritiesTab = activeTab === "securities";

  const [securitiesFilters, setSecuritiesFilters] = useState<SecuritiesFilters>(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (dataOperations) {
      try {
        return JSON.parse(dataOperations) as SecuritiesFilters;
      } catch {
        // fall through
      }
    }
    return defaultSecuritiesFilters;
  });

  // Securities filters → URL (only on securities sub-tab; clear when leaving).
  // Skip while we're about to redirect away from the root path.
  useEffect(() => {
    if (isConsolidationRoot) return;
    if (!isOnSecuritiesTab || isEqual(securitiesFilters, defaultSecuritiesFilters)) {
      removeParams(["dataOperations"]);
      return;
    }
    setParams({ search: { dataOperations: JSON.stringify(securitiesFilters) } });
  }, [securitiesFilters, isOnSecuritiesTab, isConsolidationRoot]);

  // URL → securities filters when navigating between bookmarks while staying on this page
  useEffect(() => {
    if (isConsolidationRoot || !isOnSecuritiesTab) return;
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (!dataOperations) return;
    try {
      const fromUrl = JSON.parse(dataOperations) as SecuritiesFilters;
      setSecuritiesFilters((prev) => (isEqual(prev, fromUrl) ? prev : fromUrl));
    } catch {
      // ignore
    }
  }, [queryParams.dataOperations, isOnSecuritiesTab, isConsolidationRoot]);
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();

  const {
    getSecurityTableAction,
    uploadSecurityDataAction,
    getSecuritiesMeasuresMapAction,
    saveSecuritiesMeasuresMapAction,
    getAssetClassMappingsAction,
    saveAssetClassMappingsAction,
    updateSecurityConsolidationAction,
    undoSecurityConsolidationAction,
    getDealsTableRowsAction,
    getMeasureParametersAction,
    updateDealAction
  } = useDealActions();
  const socket: ISocketService = useSocket();
  const userId: number | undefined = useUserIdSelector();
  const { id: accountId } = useAccountSelector() || {};
  const { assetClasses } = useReportsMetaSelector();

  const isMappingTable = activeTab === "measures" || activeTab === "assetClasses";
  const {
    data: measures, //array of measures without formula
    loading: measuresLoading,
    refetch: refetchMeasures
  } = useQueryHook<MeasureParameter[]>(getMeasureParametersAction);

  const {
    data: securityTableData,
    isLoading: securityTableLoading,
    refetch: refetchSecurityTable
  } = useQuery<ISecurityTableRow[]>(["security-table", securitiesFilters], () =>
    getSecurityTableAction(securitiesFilters)
  );

  const {
    data: measureMappingsData,
    loading: measureMappingsLoading,
    refetch: refetchMeasureMappings
  } = useQueryHook<IMeasureMapping[]>(getSecuritiesMeasuresMapAction);

  const {
    data: assetClassMappingsData,
    loading: assetClassMappingsLoading,
    refetch: refetchAssetClassMappings
  } = useQueryHook<IAssetClassMapping[]>(getAssetClassMappingsAction);

  const { action: saveMeasureMappings, loading: savingMeasures } = useActionHook(saveSecuritiesMeasuresMapAction, {
    onSuccess: () => {
      refetchMeasureMappings();
      refetchSecurityTable();
    }
  });

  const { action: saveAssetClassMappings, loading: savingAssetClasses } = useActionHook(saveAssetClassMappingsAction, {
    onSuccess: () => {
      refetchAssetClassMappings();
      refetchSecurityTable();
    }
  });

  const { data: deals, refetch: refetchDeals } = useQueryHook<IDealsTableRow[]>(getDealsTableRowsAction);

  const { action: uploadSecurityData } = useActionHook(uploadSecurityDataAction);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isBankUploadOpen, setIsBankUploadOpen] = useState(false);

  const [itemMappings, setItemMappings] = useState<ConsolidationMapping[]>([]);
  const [originalItemMappings, setOriginalItemMappings] = useState<ConsolidationMapping[]>([]);

  const { action: updateSecurityConsolidation } = useActionHook(updateSecurityConsolidationAction, {
    onSuccess: () => {
      refetchSecurityTable();
    }
  });

  const { action: undoSecurityConsolidation } = useActionHook(undoSecurityConsolidationAction, {
    onSuccess: () => {
      refetchSecurityTable();
    }
  });

  const { action: updateDealAssetClass } = useActionHook(
    async (dealId: number, assetClassId: number) => {
      await updateDealAction({ dealId, asset_class_id: assetClassId });
    },
    {
      onSuccess: () => {
        toast.success("Asset class updated successfully");
        refetchSecurityTable();
      }
    }
  );

  const consolidationMappings = useMemo<ConsolidationMapping[]>(() => {
    if (!measureMappingsData || !assetClassMappingsData) return [];

    const itemMapping =
      activeTab === "measures"
        ? measureMappingsData.map((mapping) => ({
            itemId: mapping.measure_id,
            itemName: mapping.measure_name,
            consolidatedName: mapping.consolidated_name,
            bankName: mapping.bank_name
          }))
        : assetClassMappingsData.map((mapping) => ({
            itemId: mapping.asset_class_id,
            itemName: mapping.asset_class_name,
            consolidatedName: mapping.consolidated_name,
            bankName: mapping.bank_name
          }));
    return itemMapping.sort((a, b) => a.itemName.localeCompare(b.itemName));
  }, [measureMappingsData, assetClassMappingsData, activeTab]);

  const items = activeTab === "measures" ? measures : assetClasses;

  useEffect(() => {
    if (consolidationMappings?.length) {
      setItemMappings(consolidationMappings);
      setOriginalItemMappings(consolidationMappings);
    }
  }, [consolidationMappings]);

  const channelName: string = useMemo(() => `private-notifications.${userId}.${accountId}`, [userId, accountId]);

  useEffect(() => {
    if (userId) {
      const channel = socket.subscribe(channelName);

      channel.bind("notifications.import.finished", () => {
        refetchMeasures();
        refetchSecurityTable();
        refetchMeasureMappings();
        refetchAssetClassMappings();
      });

      return () => {
        channel.unbind_all();
        socket.unsubscribe(channelName);
      };
    }
  }, [userId, socket]);

  const hasItemMappingChanges = useMemo(() => {
    return JSON.stringify(itemMappings) !== JSON.stringify(originalItemMappings);
  }, [itemMappings, originalItemMappings]);

  const handleSaveMapping = async () => {
    try {
      const changedItemMappings = itemMappings.filter((mapping) => {
        const original = originalItemMappings.find(
          (m) => m.consolidatedName === mapping.consolidatedName && m.bankName === mapping.bankName
        );
        return original?.itemId !== mapping.itemId;
      });

      if (activeTab === "measures") {
        await saveMeasureMappings(
          changedItemMappings.map((mapping) => ({
            measure_id: mapping.itemId,
            measure_name: mapping.itemName,
            consolidated_name: mapping.consolidatedName,
            bank_name: mapping.bankName || ""
          }))
        );
      } else {
        await saveAssetClassMappings(
          changedItemMappings.map((mapping) => ({
            asset_class_id: mapping.itemId,
            asset_class_name: mapping.itemName,
            consolidated_name: mapping.consolidatedName,
            bank_name: mapping.bankName || ""
          }))
        );
      }
      toast.success("Measure mappings saved successfully");
    } catch (error) {
      console.error("Failed to save measure mappings:", error);
      toast.error("Failed to save measure mappings. Please try again.");
    }
  };

  const handleItemMappingChange = useCallback((consolidatedName: string, bankName: string, newValue: number | null) => {
    setItemMappings((prev) =>
      prev.map((mapping) =>
        mapping.consolidatedName === consolidatedName && mapping.bankName === bankName
          ? {
              ...mapping,
              itemId: newValue,
              originalItemId: originalItemMappings.find(
                (m) => m.consolidatedName === mapping.consolidatedName && m.bankName === mapping.bankName
              )?.itemId
            }
          : mapping
      )
    );
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadClick = () => {
    if (isAnalystViewerPermission) {
      openPrompt({ Component: Notice, props: { type: "trial" } });
      return;
    }
    handleMenuClose();
    fileInputRef.current?.click();
  };

  const handleBankUploadClick = () => {
    if (isAnalystViewerPermission) {
      openPrompt({ Component: Notice, props: { type: "trial" } });
      return;
    }
    handleMenuClose();
    setIsBankUploadOpen(true);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        await uploadSecurityData(file);
        toast.success("Security data is uploading, please wait...");
      } catch (error) {
        toast.error((error as AltiusErrors)?.message || "Failed to upload security data");
        console.error("Failed to upload security data:", error);
      }
    }
    event.target.value = "";
  };

  if (isConsolidationRoot) {
    return <Redirect to={Routes.consolidationSecurities} />;
  }

  return (
    <AsyncContentMui
      isLoading={securityTableLoading || measureMappingsLoading || assetClassMappingsLoading || measuresLoading}
    >
      <Stack sx={{ height: "100%", overflow: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <HorizontalNavBar
            tabs={[
              {
                value: "securities",
                label: "Securities",
                onClick: () => navigate({ route: "consolidationSecurities", setQueriesMethod: "keep" }),
                matchRouts: [Routes.consolidationSecurities]
              },
              {
                value: "measures",
                label: "Measures",
                onClick: () => navigate({ route: "consolidationMeasures", setQueriesMethod: "replace" }),
                matchRouts: [Routes.consolidationMeasures]
              },
              {
                value: "assetClasses",
                label: "Asset Classes",
                onClick: () => navigate({ route: "consolidationAssetClasses", setQueriesMethod: "replace" }),
                matchRouts: [Routes.consolidationAssetClasses]
              }
            ]}
          />
          <Box>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept=".csv,.xlsx,.xls"
            />
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleUploadClick}>Upload security data</MenuItem>
              <MenuItem onClick={handleBankUploadClick}>Upload bank statements</MenuItem>
            </Menu>
          </Box>
        </Box>
        <Paper sx={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {isMappingTable ? (
            consolidationMappings.length > 0 ? (
              <MappingTable
                key={activeTab}
                itemType={activeTab === "measures" ? "Measure" : "Asset Class"}
                mappings={consolidationMappings}
                items={items ?? []}
                onMappingChange={handleItemMappingChange}
              />
            ) : activeTab === "measures" ? (
              <EmptyCard
                title={EMPTY_CARD_CONTENT.consolidationMeasures.title}
                description={{
                  text: EMPTY_CARD_CONTENT.consolidationMeasures.description,
                  externalText: EMPTY_CARD_CONTENT.consolidationMeasures.externalDescription
                }}
                image={{
                  src: EmptyCardList,
                  sx: { width: 315, height: 140 }
                }}
                fitPage
              />
            ) : (
              <EmptyCard
                title={EMPTY_CARD_CONTENT.consolidationAssetClasses.title}
                description={{
                  text: EMPTY_CARD_CONTENT.consolidationAssetClasses.description,
                  externalText: EMPTY_CARD_CONTENT.consolidationAssetClasses.externalDescription
                }}
                image={{
                  src: EmptyCardAssets,
                  sx: { width: 315, height: 140 }
                }}
                fitPage
              />
            )
          ) : (
            <SecuritiesTable
              rows={securityTableData ?? []}
              filters={securitiesFilters}
              setFilters={setSecuritiesFilters}
              updateSecurityConsolidation={updateSecurityConsolidation}
              undoSecurityConsolidation={undoSecurityConsolidation}
              dealList={deals ?? []}
              refetchDeals={refetchDeals}
              onAssetClassChange={updateDealAssetClass}
            />
          )}
          {isMappingTable && consolidationMappings.length > 0 && (
            <Button
              variant="contained"
              onClick={handleSaveMapping}
              disabled={!hasItemMappingChanges || savingMeasures || savingAssetClasses}
              sx={{ minWidth: 120, position: "absolute", bottom: "32px", zIndex: 1 }}
            >
              {(activeTab === "measures" && savingMeasures) || (activeTab === "assetClasses" && savingAssetClasses)
                ? "Saving..."
                : "Save"}
            </Button>
          )}
        </Paper>
      </Stack>
      <BankStatementUploadModal open={isBankUploadOpen} onClose={() => setIsBankUploadOpen(false)} />
    </AsyncContentMui>
  );
};
